import React from 'react';
import contentfulClient from './createContentfulClient';
import { CONTENTFUL_COMPONENTS, BUTTON_BG_LEGACY } from '@/utils/constants';
import ReactHTMLParser from 'react-html-parser';
import ReactDOMServer from 'react-dom/server';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { marked } from 'marked';
import {
  getCdrEntryBindingRichTextOptions,
  replaceCdrEntryBindingTokensInContentfulData
} from '@/utils/contentfulRichTextBindings';
import { mapProductComparisonFromContentful } from '@/utils/productComparisonContentfulData';

const RICH_TEXT_TARGET_NODE_TYPES = new Set([
  'embedded-entry-inline',
  'entry-hyperlink',
  'asset-hyperlink',
  'embedded-resource-inline',
  'resource-hyperlink',
  'embedded-entry-block',
  'embedded-asset-block',
  'embedded-resource-block'
]);

const ensureRichTextTargetId = node => {
  if (!node || typeof node !== 'object') return node;

  if (!RICH_TEXT_TARGET_NODE_TYPES.has(node.nodeType)) return node;

  const target = node?.data?.target;
  const targetId =
    target?.sys?.id || target?.sys?.urn || target?.id || 'missing-target';

  return {
    ...node,
    data: {
      ...(node.data || {}),
      target: {
        ...(target || {}),
        sys: {
          ...(target?.sys || {}),
          id: targetId
        }
      }
    }
  };
};

const sanitizeRichTextDocument = value => {
  if (!value || typeof value !== 'object') return value;

  if (Array.isArray(value)) {
    return value.map(sanitizeRichTextDocument);
  }

  const sanitized = Object.keys(value).reduce((acc, key) => {
    acc[key] = sanitizeRichTextDocument(value[key]);
    return acc;
  }, {});

  if (sanitized.nodeType) {
    return ensureRichTextTargetId(sanitized);
  }

  return sanitized;
};

const ImportantInfoRTConfig = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => <>{children}</>
  }
};

const TravelHowToClaimRTConfig = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => <span>{children}</span>,
    [INLINES.EMBEDDED_ENTRY]: node => {
      const target = node?.data?.target;

      if (target?.contentType?.sys?.id !== 'menuItems') return null;

      const linkProps = target?.link?.startsWith('http')
        ? { target: '_blank', rel: 'noopener noreferrer' }
        : {};

      return (
        <a href={target?.link} {...linkProps}>
          {target?.label || target?.title}
        </a>
      );
    }
  }
};

const parseTrackEventData = value => {
  if (!value) return undefined;

  if (typeof value !== 'string') return value;

  try {
    return JSON.parse(value);
  } catch (error) {
    return undefined;
  }
};

const mapTravelHowToClaimTab = tab => ({
  ...tab,
  id: tab?.tabId,
  steps: tab?.steps
    ?.map(step => getStaticMarkup(step?.content, TravelHowToClaimRTConfig))
    .filter(Boolean)
    .map(step => step.join('')),
  cta: tab?.cta && {
    ...tab.cta,
    href: tab.cta.link,
    text: tab.cta.label || tab.cta.title,
    trackEventData: parseTrackEventData(tab.cta.trackEventData)
  }
});

const renderBrokerLoanRichText = value => {
  if (!value) return value;
  if (typeof value === 'string') return value;

  return getStaticMarkup(value)?.join('');
};

const mapBrokerLoanCardCta = card => {
  const cta = card?.cta || {};

  return {
    ...cta,
    label: cta.label || card?.ctaLabel,
    ctaUrl: cta.ctaUrl || card?.ctaUrl,
    ctaUrlEnvKey: cta.ctaUrlEnvKey || card?.ctaUrlEnvKey,
    target: cta.target || card?.ctaTarget,
    trackId: cta.trackId || card?.trackId,
    trackEventData: parseTrackEventData(
      cta.trackEventData || card?.trackEventData
    ),
    trackProductIds: cta.trackProductIds || card?.trackProductIds
  };
};

const richTextListToStrings = richTextDoc => {
  if (!richTextDoc || richTextDoc.nodeType !== 'document') return richTextDoc;
  const listNode = richTextDoc.content?.find(
    n => n.nodeType === 'unordered-list' || n.nodeType === 'ordered-list'
  );
  if (!listNode) return null;
  return listNode.content.map(listItem =>
    ReactDOMServer.renderToStaticMarkup(
      documentToReactComponents(
        { nodeType: 'document', data: {}, content: listItem.content },
        {
          renderNode: {
            [BLOCKS.PARAGRAPH]: (node, children) => <>{children}</>
          }
        }
      )
    )
  );
};

const mapInterestFreeCard = card => ({
  ...card,
  listItems: richTextListToStrings(card.listItems)
});

const mapInterestFreeCards = item => ({
  ...item,
  cards: item.cards?.map(mapInterestFreeCard)
});

const mapBrokerLoanComparison = item => ({
  ...item,
  contentfulID: item?.id,
  loanCards: item?.loanCards?.map(card => ({
    ...card,
    description: renderBrokerLoanRichText(card?.description),
    cta: mapBrokerLoanCardCta(card),
    rateBoxes: card?.rateBoxes?.map(rateBox => ({ ...rateBox })),
    accordionSections: card?.accordionSections?.map(section => ({
      ...section,
      content: section?.content
    }))
  }))
});

const getStaticMarkup = (CFRichTextObj, config) =>
  CFRichTextObj
    ? documentToReactComponents(
        CFRichTextObj,
        getCdrEntryBindingRichTextOptions(config)
      ).map(item => ReactDOMServer.renderToStaticMarkup(item))
    : undefined;

function getFieldsData(data) {
  if (Array.isArray(data)) {
    return data.map(getFieldsData);
  }

  if (typeof data === 'object' && data !== null) {
    if (data.fields) {
      const fieldsWithId = {
        ...data.fields,
        id: data.sys.id,
        contentType: data.sys.contentType
      };
      return getFieldsData(fieldsWithId);
    } else {
      const result = {};
      for (const key in data) {
        if (data.hasOwnProperty(key)) {
          result[key] = getFieldsData(data[key]);
        }
      }
      return result;
    }
  }
  return data;
}

const getPageDataByPath = async path => {
  if (path.endsWith('.html') || path.endsWith('.html/')) {
    path = path.replace(/\/$/, '');
  } else if (!path.endsWith('/')) {
    path += '/';
  }

  try {
    const response = await contentfulClient.getEntries({
      'fields.path': path,
      content_type: 'page',
      include: 6
    });
    const normalizedResponse = sanitizeRichTextDocument(
      getFieldsData(response.items)
    );

    return replaceCdrEntryBindingTokensInContentfulData(normalizedResponse);
  } catch (error) {
    console.log('Error fetching contentful entries', error);
    return [];
  }
};

const formatDataFromContentful = pageData => {
  const { template, body, ...rest } = pageData || {};
  const { header, footer, alert, ...restOfTemplate } = template || {};

  //format contentful navTreeData to match header component
  const updatedHeader = header && {
    ...header,
    navTreeData: header.navTreeData && {
      ...header.navTreeData,
      items: header.navTreeData.items?.map(navGroup => ({
        ...navGroup,
        items: navGroup.items?.map(navSubGroup => navSubGroup.items)
      }))
    }
  };

  //format contentful footer data to match Footer component
  const updatedFooter = {
    ...footer,
    finePrint: footer?.finePrint && marked(footer?.finePrint),
    footerLinks: footer?.footerLinks?.links,
    appLinks:
      (footer?.appLinks && {
        downloadLabel:
          footer?.appLinks?.header && marked(footer?.appLinks?.header),
        appleLink: footer?.appLinks?.links?.filter(
          link => link?.label === 'Apple Link'
        )?.[0]?.link,
        googleLink: footer?.appLinks?.links?.filter(
          link => link?.label === 'Google Link'
        )?.[0]?.link
      }) ||
      undefined,
    socialLinks: footer?.socialLinks?.links,
    siteLinks: footer?.siteLinks?.map(siteLink => ({
      header: siteLink?.header,
      menuItems: siteLink?.links?.map(link => ({
        label: link?.label,
        link: link?.link
      }))
    })),
    cardData: footer?.cardDataText
      ? {
          text: footer?.cardDataText,
          href: footer?.cardDataUrl,
          img: footer?.cardDataImage?.file?.url
        }
      : undefined,
    hideSocials: !footer.socialLinks.links,
    hideSiteLinks: !footer.siteLinks
  };

  //format contentful components
  const updatedBody = body?.reduce(
    (acc, item) => {
      const {
        contentType: {
          sys: { id }
        }
      } = item;
      switch (id) {
        case CONTENTFUL_COMPONENTS.ALERT:
          acc.alertData = acc.alertData || item;
          break;
        case CONTENTFUL_COMPONENTS.HERO_BANNER_CAROUSEL:
          acc.heroBannerData.push(item);
          break;
        case CONTENTFUL_COMPONENTS.INTEREST_FREE_OFFERS:
          acc.interestFreeOffersData.push(item);
          break;
        case CONTENTFUL_COMPONENTS.FEATURE_SLIDER:
          const updatedItem = {
            name: item.name,
            contentfulID: item?.id,
            heading: marked(item.heading || ''),
            backgroundColor: item.backgroundColor,
            description: ReactHTMLParser(marked(item.description || '')),
            contentAlign: item.contentAlign || 'center',
            featureCards: item.featureCards?.map(card => ({
              ...card,
              text: marked(card.text || '')
            }))
          };
          acc.featureSliderData.push(updatedItem);
          break;
        case CONTENTFUL_COMPONENTS.PROMO_BANNER:
          acc.promoBannerData.push(item);
          break;
        case CONTENTFUL_COMPONENTS.OFFER_BANNER:
          acc.offerBannerData.push(item);
          break;
        case CONTENTFUL_COMPONENTS.FEATURE_COLUMN:
          acc.featureColsData.push(item);
          break;
        case CONTENTFUL_COMPONENTS.RATES_AND_FEES:
          acc.ratesAndFeesData.push(item);
          break;
        case CONTENTFUL_COMPONENTS.IN_PAGE_NAV:
          const { id, ...rest } = item;
          const updatedPageNav = {
            ...rest,
            contentfulID: id,
            ctaType: rest?.ctaType ? BUTTON_BG_LEGACY[rest.ctaType] : 'primary'
          };
          acc.inPageNavData.push(updatedPageNav);
          break;
        case CONTENTFUL_COMPONENTS.STANDALONE_TEXT:
          acc.standaloneTextData.push(item);
          break;
        case CONTENTFUL_COMPONENTS.SECONDARY_NAV:
          const navItems = item.navItems.map(item => ({
            ...item,
            href: item.link
          }));
          acc.secondaryNavData = {
            ...item,
            navItems
          };
          break;
        case CONTENTFUL_COMPONENTS.IMPORTANT_INFO:
          const {
            title,
            icon,
            importantInformationSectionOne,
            importantInformationSectionTwo,
            additionalPaymentsDisclaimerOne,
            additionalPaymentsDisclaimerTwo
          } = item;

          const updatedImportantInfo = {
            title,
            icon,
            contentfulID: item?.id,
            data: {
              content: {
                importantInformationSectionOne: getStaticMarkup(
                  importantInformationSectionOne,
                  ImportantInfoRTConfig
                ),
                importantInformationSectionTwo: getStaticMarkup(
                  importantInformationSectionTwo,
                  ImportantInfoRTConfig
                ),
                additionalPaymentsDisclaimerOne: getStaticMarkup(
                  additionalPaymentsDisclaimerOne,
                  ImportantInfoRTConfig
                ),
                additionalPaymentsDisclaimerTwo: getStaticMarkup(
                  additionalPaymentsDisclaimerTwo,
                  ImportantInfoRTConfig
                )
              }
            },
            sectionOneColummClasses: importantInformationSectionTwo
              ? 'col-10 offset-1 col-lg-5 offset-lg-1'
              : 'col-10 offset-1',
            sectionTwoColummClasses: importantInformationSectionTwo
              ? 'col-10 offset-1 col-lg-5 offset-lg-0'
              : ''
          };
          acc.importantInfoData.push(updatedImportantInfo);
          break;
        case CONTENTFUL_COMPONENTS.HOW_TO_APPLY:
          acc.howToApplyData.push({
            ...item,
            contentfulID: item?.id
          });
          break;
        case CONTENTFUL_COMPONENTS.HOW_TO_APPLY_REBRAND_V2:
          acc.howToApplyRebrandV2Data.push({
            ...item,
            contentfulID: item?.id
          });
          break;
        case CONTENTFUL_COMPONENTS.TRAVEL_HOW_TO_CLAIM:
          acc.travelHowToClaimData.push({
            ...item,
            contentfulID: item?.id,
            tabs: item?.tabs?.map(mapTravelHowToClaimTab)
          });
          break;
        case CONTENTFUL_COMPONENTS.FAQ:
          let updatedFaq = {};
          if (item?.data?.length === 1) {
            updatedFaq = {
              contentfulID: item?.id,
              title: item?.title,
              description: item?.description,
              cta: item?.cta,
              data: item?.data?.[0]?.qa?.map(({ q, a, ...rest }) => ({
                ...rest,
                title: q,
                content: getStaticMarkup(a)?.join('')
              }))
            };
          } else {
            updatedFaq = {
              contentfulID: item?.id,
              title: item?.title,
              description: item?.description,
              cta: item?.cta,
              data: item?.data?.map(({ qa, ...rest }) => ({
                ...rest,
                qa: qa?.map(({ q, a, ...rest }) => ({
                  ...rest,
                  q,
                  a: getStaticMarkup(a)?.join('')
                }))
              }))
            };
          }
          acc.faqData.push(updatedFaq);
          break;
        case CONTENTFUL_COMPONENTS.RETAILER_PARTNER_LIST:
          acc.partnersListData.push(item);
          break;

        case CONTENTFUL_COMPONENTS.CARD_COLLECTION:
          acc.cardCollectionData.push(item);
          break;

        case CONTENTFUL_COMPONENTS.PROOF_POINT_COLLECTION:
          acc.proofPointCollectionData.push(item);
          break;

        case CONTENTFUL_COMPONENTS.IF_EXPLAINER:
          acc.ifExplainerCollectionData.push(item);
          break;

        case CONTENTFUL_COMPONENTS.HOW_IT_WORKS:
          acc.howItWorksData.push(item);
          break;

        case CONTENTFUL_COMPONENTS.TEXT_IMAGE_APP_LINK:
          acc.textImageAppLinkCollectionData.push(item);
          break;
        case CONTENTFUL_COMPONENTS.REWARDS_CAROUSEL:
          acc.rewardsCarouselData.push(item);
          break;
        case CONTENTFUL_COMPONENTS.FEATURE_CARD_LIST_REBRAND:
          acc.featureCardListRebrandData.push(item);
          break;
        case CONTENTFUL_COMPONENTS.CARD_FILTER_LIST_REBRAND:
          acc.cardFilterListRebrandData.push({
            ...item,
            contentfulID: item?.id
          });
          break;
        case CONTENTFUL_COMPONENTS.ADDITIONAL_CARDS_REBRAND:
          acc.additionalCardsRebrandData.push({
            ...item,
            contentfulID: item?.id
          });
          break;
        case CONTENTFUL_COMPONENTS.OFFER_SELECTOR:
          acc.offerSelectorData.push(item);
          break;
        case CONTENTFUL_COMPONENTS.PRODUCT_COMPARISON:
          acc.productComparisonData.push(
            mapProductComparisonFromContentful(item)
          );
          break;
        case CONTENTFUL_COMPONENTS.BROKER_LOAN_COMPARISON:
          acc.brokerLoanComparisonData.push(mapBrokerLoanComparison(item));
          break;

        case CONTENTFUL_COMPONENTS.INTEREST_FREE_CARDS:
          acc.interestFreeCardsData.push(mapInterestFreeCards(item));
          break;

        default:
          break;
      }
      return acc;
    },
    {
      alertData: null,
      heroBannerData: [],
      interestFreeOffersData: [],
      featureSliderData: [],
      promoBannerData: [],
      offerBannerData: [],
      featureColsData: [],
      ratesAndFeesData: [],
      inPageNavData: [],
      standaloneTextData: [],
      secondaryNavData: null,
      importantInfoData: [],
      howToApplyData: [],
      howToApplyRebrandV2Data: [],
      travelHowToClaimData: [],
      faqData: [],
      partnersListData: [],
      cardCollectionData: [],
      proofPointCollectionData: [],
      ifExplainerCollectionData: [],
      howItWorksData: [],
      textImageAppLinkCollectionData: [],
      rewardsCarouselData: [],
      featureCardListRebrandData: [],
      cardFilterListRebrandData: [],
      additionalCardsRebrandData: [],
      offerSelectorData: [],
      productComparisonData: [],
      brokerLoanComparisonData: [],
      interestFreeCardsData: []
    }
  ) || {
    alertData: null,
    heroBannerData: [],
    interestFreeOffersData: [],
    featureSliderData: [],
    promoBannerData: [],
    offerBannerData: [],
    featureColsData: [],
    ratesAndFeesData: [],
    inPageNavData: [],
    standaloneTextData: [],
    secondaryNavData: null,
    importantInfoData: [],
    howToApplyData: [],
    howToApplyRebrandV2Data: [],
    travelHowToClaimData: [],
    faqData: [],
    partnersListData: [],
    cardCollectionData: [],
    proofPointCollectionData: [],
    ifExplainerCollectionData: [],
    howItWorksData: [],
    textImageAppLinkCollectionData: [],
    rewardsCarouselData: [],
    featureCardListRebrandData: [],
    cardFilterListRebrandData: [],
    additionalCardsRebrandData: [],
    offerSelectorData: [],
    productComparisonData: [],
    brokerLoanComparisonData: [],
    interestFreeCardsData: []
  };

  return {
    ...rest,
    body,
    updatedBody: {
      ...updatedBody,
      alertData: updatedBody.alertData || alert
    },
    template: {
      ...restOfTemplate,
      header: updatedHeader,
      footer: updatedFooter
    }
  };
};

export { getPageDataByPath, formatDataFromContentful };
